'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataTableDefaults } from '../data/table-defaults-context';
import { ConfirmCardModal } from './confirm-card-modal';
/**
 * "Discard changes?" for a form modal (modern modules only).
 *
 * Dirty = the user typed/picked something since the modal opened: native change events
 * inside the content (trackChange) + AdaptiveSelectField via FormDirtyContext (Provider).
 * Wire it up as:
 *   <FormDirtyContext.Provider value={guard.dirtyContextValue}>
 *     <Modal onClose={guard.requestClose}>            ← ✕ / Esc / overlay
 *       <div onChangeCapture={guard.trackChange}>…</div>
 *       <ModalFooter onClickCapture={guard.interceptCancel}>
 *         <button data-modal-cancel onClick={onClose}>Cancel</button> …
 *     </Modal>
 *     {guard.dialog}
 *   </FormDirtyContext.Provider>
 * Saving closes through the page's own onClose, so it never prompts.
 * Forms whose edits don't fire change events (buttons, custom pickers) call markDirty();
 * a modal that stays open after saving calls markClean().
 */
export function useDiscardGuard({ isOpen, onClose, enabled = true }) {
    const guardChanges = useDataTableDefaults().appearance === 'modern' && enabled;
    const [isDirty, setIsDirty] = useState(false);
    const [askDiscard, setAskDiscard] = useState(false);
    const markDirty = useCallback(() => setIsDirty(true), []);
    const markClean = useCallback(() => setIsDirty(false), []);
    useEffect(() => {
        if (isOpen) {
            setIsDirty(false);
            setAskDiscard(false);
        }
    }, [isOpen]);
    const requestClose = () => {
        if (guardChanges && isDirty)
            setAskDiscard(true);
        else
            onClose();
    };
    const trackChange = guardChanges
        ? (e) => {
            // Typing in a dropdown's search box is not a change (picking an option is, via the context)
            if (e.target.getAttribute('aria-autocomplete') === 'list')
                return;
            // Search / filter boxes that only narrow what is shown opt out with data-discard-ignore
            if (e.target.closest('[data-discard-ignore]'))
                return;
            setIsDirty(true);
        }
        : undefined;
    const interceptCancel = guardChanges
        ? (e) => {
            if (!isDirty || !e.target.closest('[data-modal-cancel]'))
                return;
            // Capture phase: stop the Cancel button's own onClose and ask first
            e.stopPropagation();
            e.preventDefault();
            setAskDiscard(true);
        }
        : undefined;
    const dialog = guardChanges ? (_jsx(ConfirmCardModal, { isOpen: askDiscard, onClose: () => setAskDiscard(false), onConfirm: () => { setAskDiscard(false); setIsDirty(false); onClose(); }, title: "Discard changes?", message: "You have unsaved changes. If you close now, they will be lost.", tone: "warning", confirmText: "Discard", cancelText: "Keep editing", zIndexClass: "z-[70]" })) : null;
    return { requestClose, trackChange, interceptCancel, dirtyContextValue: guardChanges ? markDirty : null, markDirty, markClean, dialog };
}
// The history copy a guarded page pushes (for browser Back) stays behind when the page is left
// by Save / Cancel. Landing on such a copy later with no page guarding steps over it, so Back
// from the list still goes to the form once, as before.
let activeHistoryGuards = 0;
let copySkipperInstalled = false;
function installCopySkipper() {
    if (copySkipperInstalled || typeof window === 'undefined')
        return;
    copySkipperInstalled = true;
    window.addEventListener('popstate', () => {
        if (activeHistoryGuards === 0 && window.history.state?.discardGuardCopy)
            window.history.back();
    });
}
/**
 * "Discard changes?" for a full-page form (modern modules only): asked when the user leaves
 * with Cancel / Back after changing something. Saving navigates away through the page's own
 * code, so it never prompts.
 *   const guard = usePageDiscardGuard();
 *   <FormDirtyContext.Provider value={guard.dirtyContextValue}>
 *     <div className="contents" onChangeCapture={guard.trackChange}>…form…</div>
 *     <button onClick={() => guard.guardLeave(goBack)}>Cancel</button>
 *     {guard.dialog}
 *   </FormDirtyContext.Provider>
 */
export function usePageDiscardGuard({ enabled = true } = {}) {
    const guardChanges = useDataTableDefaults().appearance === 'modern' && enabled;
    const [isDirty, setIsDirty] = useState(false);
    const [pendingLeave, setPendingLeave] = useState(null);
    const markDirty = useCallback(() => setIsDirty(true), []);
    const markClean = useCallback(() => setIsDirty(false), []);
    /** `dirty` overrides the tracked state for a page that already knows (e.g. its own "user edited" flag). */
    const guardLeave = (leave, dirty = isDirty) => {
        if (guardChanges && dirty)
            setPendingLeave(() => leave);
        else
            leave();
    };
    // Leaving some other way while there are changes asks too:
    //  - any in-app link (sidebar, landing cards) or a [data-leaves-page] button (breadcrumbs):
    //    the click is held, and Discard clicks it again;
    //  - browser Back: a copy of this page is pushed on the history, so Back lands on the same
    //    page and asks; Discard goes back for real (Keep editing pushes the copy again);
    //  - refresh / closing the tab: the browser's own "Leave site?" box.
    const passThrough = useRef(false);
    // After Back + Keep editing the history copy has been used up: put it back
    const keepEditingRearms = useRef(null);
    useEffect(() => {
        if (!guardChanges || !isDirty)
            return;
        activeHistoryGuards += 1;
        installCopySkipper();
        const onClick = (e) => {
            if (passThrough.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
                return;
            const el = e.target?.closest('a[href], [data-leaves-page]');
            if (!el)
                return;
            if (el instanceof HTMLAnchorElement) {
                if (el.target === '_blank' || el.hasAttribute('download'))
                    return;
                const url = new URL(el.href, window.location.href);
                if (url.origin !== window.location.origin)
                    return;
                if (url.pathname === window.location.pathname && url.search === window.location.search)
                    return;
            }
            e.preventDefault();
            e.stopPropagation();
            setPendingLeave(() => () => {
                passThrough.current = true;
                try {
                    el.click();
                }
                finally {
                    passThrough.current = false;
                }
            });
        };
        const armBack = () => window.history.pushState({ ...window.history.state, discardGuardCopy: true }, '', window.location.href);
        if (!window.history.state?.discardGuardCopy)
            armBack();
        const onPopState = () => {
            if (passThrough.current) {
                passThrough.current = false;
                return;
            }
            setPendingLeave(() => () => { passThrough.current = true; window.history.back(); });
            keepEditingRearms.current = armBack;
        };
        // (not after Discard: Back out of a page opened straight from the address bar unloads it)
        const onBeforeUnload = (e) => { if (passThrough.current)
            return; e.preventDefault(); e.returnValue = ''; };
        document.addEventListener('click', onClick, true);
        window.addEventListener('popstate', onPopState);
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            activeHistoryGuards -= 1;
            document.removeEventListener('click', onClick, true);
            window.removeEventListener('popstate', onPopState);
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [guardChanges, isDirty]);
    const trackChange = guardChanges
        ? (e) => {
            if (e.target.getAttribute('aria-autocomplete') === 'list')
                return;
            if (e.target.closest('[data-discard-ignore]'))
                return;
            setIsDirty(true);
        }
        : undefined;
    const dialog = guardChanges ? (_jsx(ConfirmCardModal, { isOpen: !!pendingLeave, onClose: () => { setPendingLeave(null); keepEditingRearms.current?.(); keepEditingRearms.current = null; }, onConfirm: () => { const leave = pendingLeave; keepEditingRearms.current = null; setPendingLeave(null); setIsDirty(false); leave?.(); }, title: "Discard changes?", message: "You have unsaved changes. If you leave now, they will be lost.", tone: "warning", confirmText: "Discard", cancelText: "Keep editing", zIndexClass: "z-[70]" })) : null;
    /** True while a Discard is taking the user away — a page with its own beforeunload skips it then */
    const isLeaving = useCallback(() => passThrough.current, []);
    return { guardLeave, trackChange, dirtyContextValue: guardChanges ? markDirty : null, markDirty, markClean, isDirty, isLeaving, dialog };
}
